/**
 * pickup-instructions slug — modal dim-helper copy (prefilled ShelfCycle source vs planned NuOrder SF fields).
 * @see docs/OPT_IN_ORDER_DOCUMENTS.md
 *
 * NuOrder field names in PICKUP_INSTRUCTIONS_NUORDER_SFDC_PLANNED are dev reference only — not shown in UI.
 */

type NuOrderSfdcObject = "NuOrder__c" | "NuOrder_Line_Item__c";

type NuOrderSfdcFieldRef = {
  object: NuOrderSfdcObject;
  field: string;
};

/** Dev reference: SFDC targets for manual pickup-instructions modal keys (not shown in UI). */
const PICKUP_INSTRUCTIONS_NUORDER_SFDC_PLANNED = {
  borderCrossingNotes: { object: "NuOrder__c", field: "Comments_for_Border_Crossing__c" },
  lineProducer: { object: "NuOrder_Line_Item__c", field: "Producer__c" },
  linePickUpAppointment: [
    { object: "NuOrder__c", field: "Pick_Up_Hours__c" },
    { object: "NuOrder_Line_Item__c", field: "Pickup_Time__c" },
  ],
  lineReleaseNumber: [
    { object: "NuOrder__c", field: "Release_Number__c" },
    { object: "NuOrder__c", field: "ST_Release_Number__c" },
  ],
  lineDeliveryAppointment: [
    { object: "NuOrder__c", field: "Delivery_Time__c" },
    { object: "NuOrder_Line_Item__c", field: "Delivery_Time__c" },
  ],
} as const satisfies Record<string, NuOrderSfdcFieldRef | readonly NuOrderSfdcFieldRef[]>;

type PickupInstructionsPrefilledFieldHint = {
  prefilled: true;
  shelfcycleSource: string;
};

type PickupInstructionsManualFieldHint = {
  prefilled: false;
  planned: NuOrderSfdcFieldRef | readonly NuOrderSfdcFieldRef[];
};

type PickupInstructionsFieldHint = PickupInstructionsPrefilledFieldHint | PickupInstructionsManualFieldHint;

const pickupInstructionsFieldHints = {
  documentDate: {
    prefilled: true,
    shelfcycleSource: "Prefilled from today's date (org timezone).",
  },
  carrier: {
    prefilled: true,
    shelfcycleSource: "Prefilled from order shipping carrier.",
  },
  carrierContact: {
    prefilled: true,
    shelfcycleSource: "Prefilled from carrier supplier logistics contact when exactly one is on file.",
  },
  senderName: {
    prefilled: true,
    shelfcycleSource: "Prefilled from order assignee.",
  },
  reLine: {
    prefilled: true,
    shelfcycleSource: "Prefilled from this purchase order, the linked purchase order, or the sales order number.",
  },
  pickUpLocation: {
    prefilled: true,
    shelfcycleSource: "Prefilled from linked PO ship-from location (edit when terminal differs from supplier).",
  },
  supplier: {
    prefilled: true,
    shelfcycleSource: "Prefilled from linked PO supplier name and address.",
  },
  pickUpNotes: {
    prefilled: true,
    shelfcycleSource:
      "Prefilled from resolved supplier shipping instructions (custom PO remarks or ship-from default); editable before generate.",
  },
  includeBlindPickupDelivery: {
    prefilled: true,
    shelfcycleSource: "Defaults on for blind ship orders.",
  },
  borderCrossingNotes: {
    prefilled: false,
    planned: PICKUP_INSTRUCTIONS_NUORDER_SFDC_PLANNED.borderCrossingNotes,
  },
  lineDescription: {
    prefilled: true,
    shelfcycleSource: "Prefilled from order line product description.",
  },
  lineProducer: {
    prefilled: false,
    planned: PICKUP_INSTRUCTIONS_NUORDER_SFDC_PLANNED.lineProducer,
  },
  lineQuantity: {
    prefilled: true,
    shelfcycleSource: "Prefilled from order line quantity, packaging, and gross weight (lbs) when applicable.",
  },
  linePickUpDate: {
    prefilled: true,
    shelfcycleSource: "Prefilled from linked PO ship-at date, falling back to order ship-by when PO date is absent (org timezone).",
  },
  linePickUpAppointment: {
    prefilled: true,
    shelfcycleSource: "Prefilled from order ship-by appointment time (start/end).",
  },
  lineReleaseNumber: {
    prefilled: false,
    planned: PICKUP_INSTRUCTIONS_NUORDER_SFDC_PLANNED.lineReleaseNumber,
  },
  lineDeliveryDate: {
    prefilled: true,
    shelfcycleSource: "Prefilled from order required-by date (org timezone).",
  },
  lineDeliveryAppointment: {
    prefilled: true,
    shelfcycleSource: "Prefilled from order required-by appointment time (start/end).",
  },
  consignor: {
    prefilled: true,
    shelfcycleSource: "Prefilled from org legal name and address (delivery consignor).",
  },
  consignee: {
    prefilled: true,
    shelfcycleSource: "Prefilled from ship-to address.",
  },
  deliveryNotes: {
    prefilled: true,
    shelfcycleSource:
      "Prefilled from resolved order shipping instructions (custom remarks or ship-to default), " +
      "with Customer PO# appended when order.customerOrderNo is set; editable before generate.",
  },
} as const satisfies Record<string, PickupInstructionsFieldHint>;

export type PickupInstructionsPlannedPrefillHintKey = keyof typeof pickupInstructionsFieldHints;

const MANUAL_PICKUP_FIELD_HINT = "Not prefilled from order data today.";

const formatManualPickupInstructionsFieldHint = (_hint: PickupInstructionsManualFieldHint): string => MANUAL_PICKUP_FIELD_HINT;

const formatPickupInstructionsFieldHint = (hint: PickupInstructionsFieldHint): string => {
  if (hint.prefilled) {
    return hint.shelfcycleSource;
  }
  return formatManualPickupInstructionsFieldHint(hint);
};

export const pickupInstructionsPlannedPrefillDescription = (key: PickupInstructionsPlannedPrefillHintKey): string =>
  formatPickupInstructionsFieldHint(pickupInstructionsFieldHints[key]);
